'use client';
import { useState, useEffect } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { SignUpModal } from '@/components/auth';
import { getStoredReferralCode, isSentinelRefCode } from '@/lib/signupAccessStorage';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { getReferrer } from '@/lib/api';
import { BACKGROUND_VIDEOS } from '@/lib/marketing-config';
import { Button } from '@/components/ui/button';
import { VideoCreditBadge } from '@/components/marketing/VideoCreditBadge';
// Select once at module load time (client-side only, SSR will use index 0)
const getRandomVideoIndex = ()=>Math.floor(Math.random() * BACKGROUND_VIDEOS.length);
export function HeroSection() {
    const t = useTranslations$1();
    const [isVisible, setIsVisible] = useState(false);
    // null = not yet mounted (SSR), number = selected index
    const [videoIndex, setVideoIndex] = useState<number | null>(null);
    const [referrer, setReferrer] = useState<{
        alias: string;
        color: string;
    } | null>(null);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const hasSignupAccess = useStoredSignupAccess();
    useEffect(()=>{
        // Select random video once on mount, never changes after. This has to stay in an
        // effect: videoIndex starts null so the server and the first client render agree,
        // and picking during render would hydration-mismatch on a random value.
        // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate client-only deferral, see above
        setVideoIndex(getRandomVideoIndex());
        setIsVisible(true);
        // Check for stored referral code and fetch referrer info. Sentinels
        // (motion_share, AI-assistant referrals) are never real referrers — skip
        // the guaranteed-404 lookup.
        const code = getStoredReferralCode();
        if (code && !isSentinelRefCode(code)) {
            getReferrer(code).then((data)=>{
                if (data) {
                    setReferrer(data);
                }
            }).catch(()=>{
            // Invalid referral code, ignore
            });
        }
    }, []);
    const selectedVideo = videoIndex !== null ? BACKGROUND_VIDEOS[videoIndex] : null;
    return (<section className="relative flex h-screen w-full items-center justify-center overflow-hidden">
      { /* Video Background */ }
      <div className="absolute inset-0 h-full w-full bg-black">
        {selectedVideo && (<video key={selectedVideo.src} autoPlay loop muted playsInline preload="auto" className="absolute inset-0 h-full w-full scale-110 object-cover" style={{
        objectPosition: 'center center'
    }} aria-label={t("zrzJ0M")}>
            <source src={selectedVideo.src} type="video/mp4"/>
          </video>)}
      </div>

      { /* Gradient Overlays - Subtle vignette for text readability */ }
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/25"/>
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/55"/>
      <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black/15"/>

      { /* Floating Particles */ }
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[
        ...Array(5)
    ].map((_, i)=>{
        // Use deterministic values based on index to avoid hydration mismatch
        const sizeVariation = 1 + ((i * 0.13) % 1) // 1.0 - 1.9px
        ;
        const durationVariation = 5 + ((i * 0.29) % 2) // 5 - 7s
        ;
        return (<div key={i} className="animate-float absolute rounded-full bg-white/6" style={{
            left: `${18 + i * 18}%`,
            top: `${22 + i * 15}%`,
            width: `${sizeVariation}px`,
            height: `${sizeVariation * (1 + (i % 3) * 0.1)}px`,
            animationDelay: `${i * 0.6}s`,
            animationDuration: `${durationVariation}s`
        }}/>);
    })}
      </div>

      { /* Video Credit Badge */ }
      {selectedVideo && (<div className="absolute bottom-6 end-6 z-20 md:bottom-8 md:end-8">
          <VideoCreditBadge video={selectedVideo}/>
        </div>)}

      { /* Hero Content - Center-aligned */ }
      <div className={`relative z-10 flex h-full w-full items-center px-6 pt-8 transition-opacity duration-1000 md:justify-center md:px-8 md:pt-0 lg:px-12 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="w-full max-w-4xl text-center md:max-w-5xl">
          { /* Badge with Social Proof */ }
          <div className={`mb-5 mx-auto inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-2 backdrop-blur-xl transition-all delay-100 duration-1000 md:mb-7 ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}>
            <Sparkles className="h-3.5 w-3.5 text-white/80"/>
            <span className="text-xs font-medium tracking-wide text-white/90">{t("pENeeG")}</span>
            <div className="hidden h-1 w-1 rounded-full bg-white/50 md:block"/>
            <div className="hidden items-center gap-1.5 md:flex">
              <span className="text-xs font-light text-white/70">{t("VfiEYF")}</span>
            </div>
          </div>

          { /* Main Headline */ }
          <h1 className={`mb-5 text-5xl font-bold leading-[1.05] tracking-tight transition-all delay-200 duration-1000 sm:text-6xl md:mb-7 md:text-7xl lg:text-8xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            { /* Mobile: Two lines */ }
            <span className="block text-white sm:hidden">{t("c9gU0c")}</span>
            <span className="mt-1 block text-white/95 sm:hidden">{t("CWZafR")}</span>
            { /* Desktop: Two lines with better break */ }
            <span className="hidden text-white sm:block">{t("c9gU0c")}</span>
            <span className="hidden mt-1 block text-white/95 sm:block">{t("CWZafR")}</span>
          </h1>

          { /* Subheadline */ }
          <h2 className={`mb-8 text-xl font-light leading-relaxed text-white/85 transition-all delay-300 duration-1000 sm:text-2xl md:mb-10 md:text-3xl lg:text-4xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            {t("M_GFeb")}
          </h2>

          { /* CTA Section */ }
          <div className={`mx-auto max-w-2xl transition-all delay-500 duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            {referrer ? (<p className="text-base text-white/80 mb-6 md:text-lg md:mb-7">
                <span className="font-medium" style={{
        color: referrer.color
    }}>
                  {referrer.alias}
                </span>{' '}
                {t("xvaX8a")}
              </p>) : (<p className="text-base text-white/70 mb-6 md:text-lg md:mb-7">
                {t("Og94b-")}
              </p>)}

            {referrer || hasSignupAccess ? (<Button onClick={()=>setShowSignUpModal(true)} className="group h-14 w-full cursor-pointer rounded-2xl border-0 bg-white text-base font-semibold text-black transition-all duration-300 hover:scale-[1.01] hover:bg-white/95 active:scale-[0.98] md:h-16 md:text-lg">
                <span className="flex items-center justify-center">
                  {t("PEa8RR")}
                  <ArrowRight className="ms-3 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"/>
                </span>
              </Button>) : (<Button asChild className="group h-14 w-full cursor-pointer rounded-2xl border-0 bg-white text-base font-semibold text-black transition-all duration-300 hover:scale-[1.01] hover:bg-white/95 active:scale-[0.98] md:h-16 md:text-lg">
                <a href="https://form.typeform.com/to/VVwyeUrj" target="_blank" rel="noopener noreferrer">
                  <span className="flex items-center justify-center">
                    {t("UnR8YN")}
                    <ArrowRight className="ms-3 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"/>
                  </span>
                </a>
              </Button>)}
          </div>
        </div>
      </div>

      { /* Sign Up Modal for Invited Users */ }
      <SignUpModal isOpen={showSignUpModal} onClose={()=>setShowSignUpModal(false)} returnPath="/projects" defaultMode="signup" variant="marketing"/>
    </section>);
}
